import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    CheckCircleIcon,
    ClockIcon,
    EnvelopeIcon,
    SparklesIcon,
} from "@heroicons/react/24/outline";

import logo from "./img/logo.svg";
import './landing.scss';

export default function AwaitingApprovalPage() {
    useEffect(() => {
        const htmlElement = document.documentElement;
        htmlElement.classList.add("tailwind-html");

        return () => {
            htmlElement.classList.remove("tailwind-html");
        };
    }, []);

    useEffect(() => {
        document.body.classList.add("tailwind-body");

        return () => {
            document.body.classList.remove("tailwind-body");
        };
    }, []);

    return (
        <div className="landing-page">
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-sky-50 rounded-3xl md:m-5">
                {/* Header */}
                <header className="relative z-40">
                    <nav
                        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
                        aria-label="Global"
                    >
                        <div className="flex lg:flex-1">
                            <Link to="/" className="flex items-center space-x-2">
                                <img
                                    src={logo}
                                    alt="Emissary Care"
                                    className="h-10 w-auto"
                                />
                            </Link>
                        </div>
                    </nav>
                </header>

                {/* Main Content */}
                <main className="relative isolate">
                    {/* Background Grid Pattern */}
                    <div className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] opacity-50" />

                    <div
                        className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
                        aria-hidden="true"
                    >
                        <div
                            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-sky-300 to-sky-600 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
                            style={{
                                clipPath:
                                    "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
                            }}
                        />
                    </div>

                    <div className="px-6 py-24 sm:py-32 lg:px-8">
                        <div className="mx-auto max-w-2xl text-center">
                            <div className="mb-8 flex justify-center">
                                <div className="relative rounded-full px-3 py-1 text-sm leading-6 text-gray-600 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
                                    <SparklesIcon className="inline-block h-4 w-4 mr-1" />
                                    Application Status
                                </div>
                            </div>

                            <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-sky-100 mb-8">
                                <ClockIcon className="h-12 w-12 text-sky-600" aria-hidden="true" />
                            </div>

                            <h2 className="text-4xl font-bold tracking-tight sm:text-6xl leading-tight pb-3 bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                                Application Under Review
                            </h2>

                            <p className="mt-6 text-lg leading-8 text-gray-600">
                                Thank you for your interest in joining Emissary Care. Our team is currently reviewing your application.
                            </p>

                            <div className="mt-16 flow-root">
                                <div className="rounded-2xl bg-white/80 backdrop-blur-sm shadow-lg shadow-sky-900/5 ring-1 ring-slate-100 p-8">
                                    <h3 className="text-base font-semibold leading-7 text-gray-900 mb-6 text-center">What happens next?</h3>
                                    <dl className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                                        <div className="relative flex flex-col items-center p-6 rounded-xl bg-sky-50/50 ring-1 ring-sky-100">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-sky-100 ring-1 ring-sky-200 mb-4">
                                                <EnvelopeIcon className="h-6 w-6 text-sky-600" aria-hidden="true" />
                                            </div>
                                            <div className="text-center">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">Email Confirmation</dt>
                                                <dd className="mt-2 text-sm leading-6 text-gray-600">We'll send you an email once your application has been reviewed</dd>
                                            </div>
                                        </div>
                                        <div className="relative flex flex-col items-center p-6 rounded-xl bg-sky-50/50 ring-1 ring-sky-100">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-sky-100 ring-1 ring-sky-200 mb-4">
                                                <CheckCircleIcon className="h-6 w-6 text-sky-600" aria-hidden="true" />
                                            </div>
                                            <div className="text-center">
                                                <dt className="text-sm font-medium leading-6 text-gray-900">Account Activation</dt>
                                                <dd className="mt-2 text-sm leading-6 text-gray-600">Upon approval, your account will be activated and you can start using our platform</dd>
                                            </div>
                                        </div>
                                    </dl>
                                </div>
                            </div>

                            <div className="mt-10 flex items-center justify-center gap-x-6">
                                <Link
                                    to="/auth"
                                    className="group relative rounded-lg px-6 py-3 text-sm font-semibold text-white transition-all duration-200 ease-in-out"
                                >
                                    <span className="absolute inset-0 rounded-lg bg-blue-500 transition-all duration-200 ease-in-out group-hover:bg-blue-600"></span>
                                    <span className="absolute inset-0 rounded-lg ring-2 ring-blue-400/40 transition-all duration-200 ease-in-out group-hover:ring-blue-500/40"></span>
                                    <span className="relative">Return to Homepage</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}
